import { useCallback, useEffect, useMemo } from "react";
import { signOut, type Auth } from "firebase/auth";
import type { PageKey } from "../lib/menu";
import { buildMenu } from "../lib/menu";

type Params = {
  auth: Auth;
  user: any;
  activePage: PageKey;
  setActivePage: (p: PageKey) => void;
  isCustomerPortal: boolean; // use bool for scoping (customer roles)
  filtersActive: boolean;
  authReady: boolean;
  fetchFirstPage: () => Promise<void>;
  startHomeSentinel: () => void;
  stopHomeSentinel: () => void;
  onLoggedOut?: () => void;
};

export function useAppShell({
  auth,
  user,
  isCustomerPortal,
  activePage,
  setActivePage,
  filtersActive,
  authReady,
  fetchFirstPage,
  startHomeSentinel,
  stopHomeSentinel,
  onLoggedOut,
}: Params) {
  const refreshHomeList = useCallback(async () => {
    await fetchFirstPage();
  }, [fetchFirstPage]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      onLoggedOut?.();
    }
    catch (e) { console.error("Logout misslyckades:", e); }
  }, [auth, onLoggedOut]);

  const MENU = useMemo(() => buildMenu(user), [user]);

  useEffect(() => {
    if (isCustomerPortal && activePage === "fakturering") {
      setActivePage("home");
    }
  }, [activePage, isCustomerPortal]);

  useEffect(() => {
    if (!authReady) return;

    if (activePage !== "home") {
      stopHomeSentinel();
      return;
    }
    if (filtersActive) {
      stopHomeSentinel();
      return;
    }

    startHomeSentinel();
    return () => stopHomeSentinel();
  }, [authReady, activePage, filtersActive, startHomeSentinel, stopHomeSentinel]);

  useEffect(() => {
    if (!authReady) return;
    if (activePage !== "home") return;
    fetchFirstPage();
  }, [authReady, activePage, fetchFirstPage]);

  return {
    MENU,
    handleLogout,
    refreshHomeList,
  };
}
